import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { data, redirect, useFetcher } from 'react-router'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { ServerOnly } from 'remix-utils/server-only'
import { z } from 'zod'
import { setLocale } from '#app/utils/i18n.server.ts'
import { useLocale } from '#app/utils/i18n.ts'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { type Route } from './+types/locale-switch.ts'

const LocaleFormSchema = z.object({
	locale: z.enum(['uk', 'en']),
	// this is useful for progressive enhancement
	redirectTo: z.string().optional(),
})

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: LocaleFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid locale received')

	const { locale, redirectTo } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setLocale(locale) },
	}
	if (redirectTo) {
		return redirect(safeRedirect(redirectTo, '/'), responseInit)
	} else {
		return data({ result: submission.reply() }, responseInit)
	}
}

export function LocaleSwitch() {
	const fetcher = useFetcher<typeof action>()
	const requestInfo = useRequestInfo()
	const locale = useLocale()

	const [form] = useForm({
		id: 'locale-switch',
		lastResult: fetcher.data?.result,
	})

	const nextLocale = locale === 'uk' ? 'en' : 'uk'
	const label = locale === 'uk' ? 'EN' : 'УКР'

	return (
		<fetcher.Form
			method="POST"
			{...getFormProps(form)}
			action="/resources/locale-switch"
		>
			<ServerOnly>
				{() => (
					<input type="hidden" name="redirectTo" value={requestInfo.path} />
				)}
			</ServerOnly>
			<input type="hidden" name="locale" value={nextLocale} />
			<button
				type="submit"
				className="flex h-8 cursor-pointer items-center justify-center px-2 text-sm font-medium"
			>
				{label}
				<span className="sr-only">
					{locale === 'uk' ? 'Switch to English' : 'Перемкнути на українську'}
				</span>
			</button>
		</fetcher.Form>
	)
}
